import { z } from "zod";
import { ExtraCategory } from "../ExtraCategory.enum";
import { IssueCode, ValidationIssue } from "./ValidationIssue";

const ExtraImpactBlockSchema = z.object({
  propertyTicker: z.string(),
  propertyName: z.string(),
  accommodationId: z.string(),
  accommodationTicker: z.string(),
  accommodationName: z.string(),
  category: z.enum(ExtraCategory),
  blockEnabled: z.boolean(),
});

export type ExtraImpactBlockProps = z.input<typeof ExtraImpactBlockSchema>;

/** An accommodation block affected by an extra change. */
export class ExtraImpactBlock {
  public readonly propertyTicker: string;
  public readonly propertyName: string;
  public readonly accommodationId: string;
  public readonly accommodationTicker: string;
  public readonly accommodationName: string;
  public readonly category: ExtraCategory;
  public readonly blockEnabled: boolean;

  private constructor(props: z.infer<typeof ExtraImpactBlockSchema>) {
    this.propertyTicker = props.propertyTicker;
    this.propertyName = props.propertyName;
    this.accommodationId = props.accommodationId;
    this.accommodationTicker = props.accommodationTicker;
    this.accommodationName = props.accommodationName;
    this.category = props.category;
    this.blockEnabled = props.blockEnabled;
  }

  static of(props: ExtraImpactBlockProps): ExtraImpactBlock {
    return new ExtraImpactBlock(ExtraImpactBlockSchema.parse(props));
  }

  /** Name to show: the accommodation name, or its ticker when the name is missing. */
  displayName(): string {
    return this.accommodationName || this.accommodationTicker;
  }

  belongsTo(propertyTicker: string): boolean {
    return this.propertyTicker === propertyTicker;
  }
}

/** One impact code of the check and the blocks where it happens. */
export class ExtraChangeImpact {
  public readonly blocks: readonly ExtraImpactBlock[];

  private constructor(public readonly issue: ValidationIssue, blocks: readonly ExtraImpactBlock[]) {
    this.blocks = Object.freeze([...blocks]);
  }

  static of(issue: ValidationIssue, blocks: readonly ExtraImpactBlock[] = []): ExtraChangeImpact {
    return new ExtraChangeImpact(issue, blocks);
  }

  get code(): string {
    return this.issue.code;
  }

  is(code: IssueCode): boolean {
    return this.issue.is(code);
  }

  isBlocking(): boolean {
    return this.issue.isBlocking();
  }

  /** Blocks span more than one property, or a property other than the current one. */
  spansOtherProperties(currentPropertyTicker: string): boolean {
    const tickers = new Set(this.blocks.map(block => block.propertyTicker));
    return tickers.size > 1 || (tickers.size === 1 && !tickers.has(currentPropertyTicker));
  }
}

/** Result of `CheckExtraChangeImpact`. */
export class ExtraChangeImpactResult {
  public readonly impacts: readonly ExtraChangeImpact[];

  private constructor(private readonly blocked: boolean, impacts: readonly ExtraChangeImpact[]) {
    this.impacts = Object.freeze([...impacts]);
  }

  static of(blocked: boolean, impacts: readonly ExtraChangeImpact[] = []): ExtraChangeImpactResult {
    return new ExtraChangeImpactResult(blocked, impacts);
  }

  static none(): ExtraChangeImpactResult {
    return ExtraChangeImpactResult.of(false);
  }

  isBlocked(): boolean {
    return this.blocked;
  }

  hasImpacts(): boolean {
    return this.impacts.length > 0;
  }

  /**
   * The impacts that block the change: it would leave a block that is on with no active ticked
   * extra. Shown alone in the blocking message.
   */
  blockingImpacts(): ExtraChangeImpact[] {
    return this.impacts.filter(impact => impact.isBlocking());
  }

  /**
   * The impacts shown in the combined confirmation dialog: the extra loses its category, covers
   * no inventory line, or is deactivated.
   */
  warningImpacts(): ExtraChangeImpact[] {
    return this.impacts.filter(impact => !impact.isBlocking());
  }

  impact(code: IssueCode): ExtraChangeImpact | undefined {
    return this.impacts.find(impact => impact.is(code));
  }
}
